import React, { useMemo } from 'react';
import { 
  Search, 
  Plus, 
  Printer, 
  MapPin, 
  Filter, 
  CheckSquare, 
  X, 
  ArrowUpRight, 
  MinusCircle, 
  QrCode 
} from 'lucide-react';
import { useStore, setState } from '../../hooks/useStore.js';

export function InventoryToolbar({ 
  onAddItem, 
  onBatchBorrow, 
  onBatchStockOut, 
  onPrintReport, 
  onPrintLabels 
}) {
  const inventory = useStore(s => s.inventory);
  const searchQuery = useStore(s => s.searchQuery) || '';
  const locationFilter = useStore(s => s.locationFilter) || 'all';
  const filter = useStore(s => s.filter) || 'all';
  const selectedInventoryIds = useStore(s => s.selectedInventoryIds) || new Set();

  const locations = useMemo(() => {
    const locSet = new Set();
    (inventory || []).forEach(item => {
      if (!item.deleted && item.location?.trim()) {
        locSet.add(item.location.trim());
      }
    });
    return Array.from(locSet).sort((a, b) => a.localeCompare(b));
  }, [inventory]);

  const selectedCount = selectedInventoryIds.size;

  const handleClearSelection = () => {
    setState({ selectedInventoryIds: new Set() });
  };

  return (
    <div className="bg-white/80 backdrop-blur-md rounded-2xl p-3 sm:p-4 border border-slate-200/80 shadow-xs mb-4 space-y-3">
      {/* Top Search & Filter Bar */}
      <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-2.5">
        {/* Search Input: full width on mobile/tablet, flex-1 on desktop */}
        <div className="relative flex-1 min-w-0">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setState({ searchQuery: e.target.value })}
            placeholder="ค้นหาชื่อสินค้า, ยี่ห้อ, ขนาด, ล็อต, กล่องย่อย..."
            className="w-full pl-10 pr-9 py-2 rounded-xl border border-slate-300 bg-white text-sm text-slate-900 font-medium placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setState({ searchQuery: '' })}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-800 rounded-full cursor-pointer"
              title="ล้างคำค้นหา"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter Controls & Action Buttons: Grid/Flex on tablet, inline on desktop */}
        <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-2.5">
          {/* Location Filter Dropdown */}
          <div className="relative flex-1 sm:w-48 md:w-52 lg:w-56 min-w-[140px]">
            <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <select
              value={locationFilter}
              onChange={(e) => setState({ locationFilter: e.target.value })}
              className="w-full pl-9 pr-7 py-2 rounded-xl border border-slate-300 bg-white text-xs sm:text-sm text-slate-900 font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 appearance-none transition-all cursor-pointer truncate"
            >
              <option value="all">ทุกสถานที่ / ตู้จัดเก็บ</option>
              {locations.map((loc) => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>

          {/* Status Filter Dropdown */}
          <div className="relative flex-1 sm:w-40 md:w-44 lg:w-48 min-w-[130px]">
            <Filter className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <select
              value={filter}
              onChange={(e) => setState({ filter: e.target.value })}
              className="w-full pl-9 pr-7 py-2 rounded-xl border border-slate-300 bg-white text-xs sm:text-sm text-slate-900 font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 appearance-none transition-all cursor-pointer truncate"
            >
              <option value="all">สถานะทั้งหมด</option>
              <option value="near-expiry">ใกล้หมดอายุ</option>
              <option value="expired">หมดอายุแล้ว</option>
              <option value="low">สต็อกเหลือน้อย</option>
            </select>
          </div>

          {/* Action Buttons: Add Item & Print */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={onAddItem}
              className="flex items-center justify-center gap-1.5 px-3.5 sm:px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white font-semibold text-xs sm:text-sm shadow-xs transition-all cursor-pointer whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              <span>เพิ่มสินค้า</span>
            </button>

            <button
              onClick={onPrintReport}
              title="พิมพ์รายงานสต็อก"
              className="p-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 active:scale-95 text-slate-700 transition-all shadow-xs cursor-pointer flex-shrink-0"
            >
              <Printer className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Batch Selection Action Bar (Appears when items are selected) */}
      {selectedCount > 0 && (
        <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2.5 animate-in fade-in duration-150">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-bold">
              <CheckSquare className="w-3.5 h-3.5" />
              <span>เลือกอยู่ {selectedCount} รายการ</span>
            </span>
            <button
              onClick={handleClearSelection}
              className="text-xs text-slate-500 hover:text-slate-700 font-medium underline cursor-pointer"
            >
              ยกเลิกการเลือก
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onBatchBorrow}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-xs shadow-xs transition-all cursor-pointer"
            >
              <ArrowUpRight className="w-3.5 h-3.5" />
              <span>ยืม ({selectedCount})</span>
            </button>

            <button
              onClick={onBatchStockOut}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold text-xs shadow-xs transition-all cursor-pointer"
            >
              <MinusCircle className="w-3.5 h-3.5" />
              <span>จ่ายออก ({selectedCount})</span>
            </button>

            <button
              onClick={onPrintLabels}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-medium text-xs shadow-xs transition-all cursor-pointer"
            >
              <QrCode className="w-3.5 h-3.5" />
              <span>พิมพ์ Barcode/QR</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
